import React from 'react';
import {
  ChatResponder,
  MessageOwner,
  TextMessage,
  ThreadMessages,
} from 'types';
import ThreadItem from '../ThreadItem';
import Divider from '../Divider/Divider';
import DownloadPDFMessage from '../DownloadPDFMessage';
import SessionEndMessage from '../SessionEndMessage';
import AgentJoinedNotification from '../AgentJoinedNotification';
import AgentTimeoutNotification from '../AgentTimeoutNotification';
import { useTranslation } from '@omilia/providers';
import { THREAD_ITEM_TYPE } from 'constants/chat';
import { getPdfOwnerNameForTranscript } from 'utils/getPdfOwnerNameForTranscript';

const Messages = ({
  thread,
  agent,
  bot,
  sendMessage,
  resendMessage,
  restartChat,
  showDownload,
  uploadFile,
  title,
  isSessionExpired,
  showAgentTimeout,
  triggerScroll,
}: {
  thread: ThreadMessages[];
  agent: ChatResponder;
  bot: ChatResponder;
  sendMessage: (message: TextMessage) => Promise<void>;
  resendMessage: (id: string) => Promise<void>;
  uploadFile: () => void;
  restartChat: () => Promise<void>;
  showDownload: boolean;
  title: string;
  isSessionExpired?: boolean;
  showAgentTimeout?: boolean;
  triggerScroll: () => void;
}) => {
  const { t } = useTranslation();

  const getAvatar = (withAgent: boolean) => {
    return withAgent ? agent?.avatar : bot?.avatar;
  };

  const shouldShowOwner = (prevMsg, currentMsg) => {
    const withResponderName =
      currentMsg.owner === MessageOwner.BOT ? bot.withName : agent.withName;

    return (
      withResponderName &&
      (!prevMsg ||
        (prevMsg?.owner !== currentMsg?.owner &&
          currentMsg?.owner !== MessageOwner.USER) ||
        prevMsg.withAgent !== currentMsg.withAgent)
    );
  };

  const shouldShowAvatar = (nextMsg, currentMsg) => {
    return (
      !nextMsg ||
      !nextMsg.message ||
      nextMsg?.owner === MessageOwner.USER ||
      nextMsg.withAgent !== currentMsg.withAgent
    );
  };

  const shouldShowStatusIcon = (nextMsg, currentMsg) => {
    return (
      currentMsg?.owner === MessageOwner.USER &&
      nextMsg?.owner !== MessageOwner.USER
    );
  };

  const getMessageOwnerName = currentMsg => {
    return currentMsg?.withAgent
      ? agent?.name || t('messages.agent')
      : bot?.name || t('messages.bot');
  };

  const transcriptOwnerLabels = {
    user: t('messages.user'),
    agent: t('messages.agent'),
    bot: t('messages.bot'),
  };

  const getPdfOwnerName = currentMsg =>
    getPdfOwnerNameForTranscript(currentMsg, agent, bot, transcriptOwnerLabels);

  const getThread = () => {
    return thread.reduce((acc, curr, i, array) => {
      if (
        curr.type === THREAD_ITEM_TYPE.AGENT_JOINED ||
        curr.type === THREAD_ITEM_TYPE.DIVIDER
      ) {
        return [...acc, curr];
      }

      return [
        ...acc,
        {
          ...curr,
          showOwner: shouldShowOwner(array[i - 1], curr),
          showAvatar: shouldShowAvatar(array[i + 1], curr),
          showStatusIcon: shouldShowStatusIcon(array[i + 1], curr),
          ownerName: getMessageOwnerName(curr),
          pdfOwnerName: getPdfOwnerName(curr),
        },
      ];
    }, []);
  };

  const renderThreadItem = (item, index) => {
    if (item.type === THREAD_ITEM_TYPE.DIVIDER) {
      return <Divider key={index} />;
    }

    if (item.type === THREAD_ITEM_TYPE.AGENT_JOINED) {
      return <AgentJoinedNotification key={index} />;
    }

    if (item.type === THREAD_ITEM_TYPE.PRELOADER) {
      return (
        <ThreadItem
          data-test="preloader-item"
          key={index}
          type={item.type}
          owner={MessageOwner.BOT}
          avatar={getAvatar(item.withAgent)}
          showAvatar
        />
      );
    }

    if (item.message) {
      return (
        <ThreadItem
          data-test="thread-item"
          key={index}
          type={item.type}
          owner={item.owner}
          message={item.message}
          uploadFile={uploadFile}
          onSelect={sendMessage}
          resendMessage={resendMessage}
          messageId={item.client_message_id}
          showAvatar={item.showAvatar}
          showOwner={item.showOwner}
          showStatusIcon={item.showStatusIcon}
          status={item.status}
          isSensitive={item.isSensitive}
          ownerName={item.ownerName}
          errorMessage={item.errorMessage}
          avatar={getAvatar(item.withAgent)}
          triggerScroll={triggerScroll}
        />
      );
    }

    return null;
  };

  return (
    <>
      {getThread().map(renderThreadItem)}
      {showAgentTimeout && <AgentTimeoutNotification />}
      {isSessionExpired && (
        <SessionEndMessage
          thread={getThread()}
          title={title}
          restartChat={restartChat}
          withTranscript={showDownload}
        />
      )}
      {showDownload && !isSessionExpired && (
        <DownloadPDFMessage thread={getThread()} title={title} />
      )}
    </>
  );
};

export default Messages;
